import { Injectable } from '@angular/core';

import { BetBuilderExpiringSubscriptionCommand, BetBuilderOfferUpdateCommand } from '@cds/push/bet-builder-commands';
import { FixtureCommandBase } from '@cds/push/fixture-commands';
import { isBetBuilderExpiringSubscriptionEnvelope, isBetBuilderOfferUpdateEnvelope } from '@frontend/sports/content-distribution/feature';
import { OddsDisplayFormat } from '@frontend/sports/types/models/user-settings';
import { UserStateService } from '@frontend/sports/user/feature';
import { Actions, OnInitEffects, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store, createSelector } from '@ngrx/store';
import { concatMap, filter, map, mergeMap, take, withLatestFrom } from 'rxjs';

import BetslipActions from '../../../base/store/actions';
import { IBetslipRootState } from '../../../base/store/state';
import { BetslipType } from '../../../core/betslip-type';
import { BaseBetslipGroupPick } from '../../../core/picks/betslip-group-pick';
import { BetslipV1Pick } from '../../../core/picks/betslip-v1-pick';
import { BetslipV2Pick } from '../../../core/picks/betslip-v2-pick';
import PickPushUpdaterService from '../../../integration/services/push-updaters/pick-push-updater.service';
import SettingsActions from '../../settings/actions';
import { settingsOddsAcceptanceSelector } from '../../settings/selectors';
import { betslipCurrentTypeSelector, betslipTypesBaseSelector } from '../../types/base/selectors';
import { betslipTypeStateSelector } from '../../types/selectors';
import BetslipPickActions from '../actions';
import { betslipPicksListSelector, selectBetslipFlattenedPicksList } from '../selectors';
import AcceptCurrentPriceHelper from '../services/accept-current-price-helper';
import PickRefresherService from '../services/pick-refresher.service';

const selectBetslipFlattenedPicksAndBaseType = createSelector(
    betslipTypesBaseSelector,
    selectBetslipFlattenedPicksList,
    (baseTypeState, pickList) => ({
        currentSelectedType: baseTypeState.currentSelectedType,
        pickList,
    }),
);
const selectBetslipGroupPicksAndBaseType = createSelector(betslipTypesBaseSelector, betslipPicksListSelector, (baseTypeState, pickList) => ({
    currentSelectedType: baseTypeState.currentSelectedType,
    pickList: pickList.filter((pick) => BaseBetslipGroupPick.isPick(pick)) as BaseBetslipGroupPick[],
}));

@Injectable()
export default class PickUpdateEffects implements OnInitEffects {
    private static PICK_UPDATE_INIT_ACTION = 'PICK_UPDATE_INIT';

    constructor(
        private actions$: Actions,
        private store: Store<IBetslipRootState>,
        private pickPushUpdater: PickPushUpdaterService,
        private pickRefresher: PickRefresherService,
        private userState: UserStateService,
    ) {}

    /**
     * If event is updated, and it is not Edit bet, update all picks for this event.
     * Event update for edit bet is handled from {@link handleEditBetPushAction}
     */
    handleEventUpdatedActionEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipActions.eventUpdate),
            concatLatestFrom(() => this.store.select(selectBetslipFlattenedPicksAndBaseType)),
            filter(([_, betslipState]) => betslipState.currentSelectedType !== BetslipType.EditBet),
            map(([action, betslipState]) => {
                const picks = betslipState.pickList;
                const message = action.message;
                const command = message.payload as FixtureCommandBase;

                const eventPicks = picks.filter((p) => BetslipV1Pick.isPick(p) && command.fixtureId === p.id.eventId) as BetslipV1Pick[];

                return BetslipPickActions.updateV1Picks({
                    pickUpdates: eventPicks
                        .map((p) => this.pickPushUpdater.updatePick(p, message))
                        .map(([pick, updateType]) => ({ pick, updateType })),
                });
            }),
        );
    });

    /**
     * If fixture is updated and it is not Edit bet, update all picks for this fixture.
     * Fixture update for edit bet is handled from {@link handleEditBetPushAction}
     */
    handleFixtureUpdatedActionEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipActions.fixtureUpdate),
            concatLatestFrom(() => this.store.select(selectBetslipFlattenedPicksAndBaseType)),
            filter(([action, betslipState]) => betslipState.currentSelectedType !== BetslipType.EditBet),
            map(([action, betslipState]) => {
                const picks = betslipState.pickList;
                const message = action.message;
                const command = message.payload as FixtureCommandBase;

                const fixturePicks = picks.filter((p) => BetslipV2Pick.isPick(p) && p.id.fixtureId === command.fixtureId) as BetslipV2Pick[];

                return BetslipPickActions.updateV2Picks({
                    pickUpdates: fixturePicks
                        .map((p) => this.pickPushUpdater.updatePick(p, message))
                        .map(([pick, updateType]) => ({ pick, updateType })),
                });
            }),
        );
    });

    handleBetBuilderGroupOfferUpdateActionEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipActions.betBuilderGroupUpdate),
            filter((action) => isBetBuilderOfferUpdateEnvelope(action.message)),
            concatLatestFrom(() => this.store.select(selectBetslipGroupPicksAndBaseType)),
            filter(([_, betslipState]) => betslipState.currentSelectedType !== BetslipType.EditBet),
            map(([action, betslipState]) => {
                const picks = betslipState.pickList;
                const message = action.message;
                const command = message.payload as BetBuilderOfferUpdateCommand;

                const betslipGroupPicks = picks.filter((p) => p.sgpId === command.sgpId);

                return BetslipPickActions.updateGroupPicks({
                    pickUpdates: betslipGroupPicks
                        .map((p) => this.pickPushUpdater.updatePick(p, message))
                        .map(([pick, updateType]) => ({ pick, updateType })),
                });
            }),
        );
    });

    /**
     * If GroupPick subscription is about to expire on backend side, we will get a notification.
     * We need to fetch the price again and recreate the GroupPick
     */
    handleBetBuilderExpiringSubscriptionEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipActions.betBuilderGroupUpdate),
            filter((action) => isBetBuilderExpiringSubscriptionEnvelope(action.message)),
            concatLatestFrom(() => this.store.select(selectBetslipGroupPicksAndBaseType)),
            filter(([action, betslipState]) => betslipState.currentSelectedType !== BetslipType.EditBet),
            concatMap(([action, betslipState]) => {
                const picks = betslipState.pickList;
                const message = action.message;
                const command = message.payload as BetBuilderExpiringSubscriptionCommand;

                const betslipGroupPicks = picks.filter((p) => p.sgpId === command.sgpId);

                return betslipGroupPicks.map((pick) => BetslipPickActions.refreshGroupPick({ pick }));
            }),
        );
    });

    /**
     * We need to update picks with latest data after refresh.
     */
    handlePickUpdateInitEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PickUpdateEffects.PICK_UPDATE_INIT_ACTION),
            take(1),
            withLatestFrom(
                this.store.select(selectBetslipFlattenedPicksList),
                this.store.select(betslipTypeStateSelector),
                this.store.select(betslipCurrentTypeSelector),
            ),
            filter(([, pickList]) => pickList.length > 0),
            mergeMap(([, pickList, betslipTypeState, selectedType]) => {
                return this.pickRefresher.refreshPicks(pickList, betslipTypeState, selectedType);
            }),
        );
    });

    /**
     * When pick is updated we need to accept the price depending on odds acceptance
     */
    acceptPriceOnPickUpdate$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPickActions.updateV1Picks, BetslipPickActions.updateV2Picks, BetslipPickActions.updateGroupPicks),
            concatLatestFrom(() => this.store.select(settingsOddsAcceptanceSelector)),
            concatLatestFrom(() => this.userState.userState$().pipe(map((state) => state.oddsFormat || OddsDisplayFormat.EU))),
            concatMap(([[action, oddAcceptance], oddsFormat]) => {
                const picks = action.pickUpdates.map((pickUpdate) => pickUpdate.pick);

                return AcceptCurrentPriceHelper.getAcceptPriceActions(picks, oddAcceptance, oddsFormat, false, !!action.byPassErrorClear);
            }),
        );
    });

    /**
     * When odd acceptance is changed we need to update accepted price of all picks.
     */
    acceptPriceOnOddsAcceptanceChange$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SettingsActions.setOddAcceptance),
            concatLatestFrom(() => this.store.select(betslipPicksListSelector)),
            concatLatestFrom(() => this.userState.userState$().pipe(map((state) => state.oddsFormat || OddsDisplayFormat.EU))),
            concatMap(([[action, pickList], oddsFormat]) => {
                return AcceptCurrentPriceHelper.getAcceptPriceActions(pickList, action.acceptance, oddsFormat, false, false);
            }),
        );
    });

    ngrxOnInitEffects(): Action {
        return { type: PickUpdateEffects.PICK_UPDATE_INIT_ACTION };
    }
}
